import { memo } from 'react';
import { useSelector } from 'react-redux';

import { type ClipType } from '@air/api/types';

import { PrivateWorkspaceAgentSelectAssetDropdownBody } from '~/components/v2/components/workspace/workspace-agent-panel/components/select-asset/PrivateWorkspaceAgentSelectAssetDropdownBody';
import { WorkspaceAgentSelectAssetStoreProvider } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetStoreContext';
import { type WorkspaceAgentSelectAssetValue } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/workspaceAgentSelectAssetTypes';
import { type PrivateWorkspaceAgentBoardAssetSearchFilters } from '~/components/v2/components/workspace/workspace-agent-panel/queries/getPrivateWorkspaceAgentBoardAssetSearchFilters';
import { centralizedBoardIdSelector, centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';

const FALLBACK_BOARD_TITLE = 'Untitled board';
const WORKSPACE_SELECT_ASSET_STORE_KEY = 'workspace';

export type PrivateWorkspaceAgentSelectAssetDropdownProps = {
  clipTypes?: readonly ClipType[];
  'data-testid'?: string;
  /** Extra `Clips.search` filters for the asset list. Board scope is applied when a board is selected. */
  filters?: PrivateWorkspaceAgentBoardAssetSearchFilters;
  id?: string;
  onSelect: (item: WorkspaceAgentSelectAssetValue | null) => void;
};

export const PrivateWorkspaceAgentSelectAssetDropdown = memo(
  ({ clipTypes, 'data-testid': testId, filters, id, onSelect }: PrivateWorkspaceAgentSelectAssetDropdownProps) => {
    const boardId = useSelector(centralizedBoardIdSelector);
    const boardTitle = useSelector(centralizedBoardTitleSelector);
    const initialState = boardId
      ? { selectedBoard: { id: boardId, title: boardTitle || FALLBACK_BOARD_TITLE } }
      : undefined;

    return (
      // The store is created once; remount when the board scope changes so search does not keep the previous board.
      <WorkspaceAgentSelectAssetStoreProvider
        initialState={initialState}
        key={boardId ?? WORKSPACE_SELECT_ASSET_STORE_KEY}
      >
        <PrivateWorkspaceAgentSelectAssetDropdownBody
          clipTypes={clipTypes}
          data-testid={testId}
          filters={filters}
          id={id}
          onSelect={onSelect}
        />
      </WorkspaceAgentSelectAssetStoreProvider>
    );
  },
);

PrivateWorkspaceAgentSelectAssetDropdown.displayName = 'PrivateWorkspaceAgentSelectAssetDropdown';
